import React, { FC, useState } from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { Tab } from '@mui/material';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { Button } from 'src/components/Button';
import { Filters } from 'src/components/Forms/CustomFormulaForm/Filters';
import { Formatting } from 'src/components/Forms/CustomFormulaForm/Formatting';
import { FormulaCalculator } from 'src/components/Forms/CustomFormulaForm/FormulaCalculator';
import { Metrics } from 'src/components/Forms/CustomFormulaForm/Metrics';
import {
  ButtonsWrapper,
  Container,
  Form,
  FormulaSettings,
  InputsWrapper,
  Tabs,
} from 'src/components/Forms/CustomFormulaForm/styled';
import { TextInput } from 'src/components/Forms/FormInputs/TextInput';
import { DeleteModal } from 'src/components/Modals/Delete';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import useBackendConnectors from 'src/hooks/useBackendConnectors';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';
import { FormattedCustomFormulaItem } from 'src/utils';
import {
  GET_FACEBOOK_METRICS,
  GET_GOOGLE_ADS_METRICS,
  GET_GOOGLE_ANALYTICS_METRICS,
  GET_SHOPIFY_METRICS,
  GET_MAGENTO_METRICS,
} from 'src/utils/gql/queries';
import {
  ConnectorType,
  Metric,
  MetricFormatType,
  MetricsByConnectorType,
  Query,
} from 'src/utils/gql/types';

export enum NumberType {
  Decimal = 'decimal',
  WholeNumber = 'whole_number',
}

enum SettingTab {
  FORMATTING,
  FILTERS,
}

export interface CustomFormulaValues {
  name: string;
  formulaItems: FormattedCustomFormulaItem[];
  format: {
    type: MetricFormatType;
    description?: string;
    prefix?: string;
    suffix?: string;
    numberType?: NumberType;
  };
}

interface CompanyFormProps {
  onSubmit: SubmitHandler<CustomFormulaValues>;
  onCancel: () => void;
  buttonLoading: boolean;
  inputLoading?: boolean;
}

export const CustomFormulaForm: FC<CompanyFormProps> = ({
  onSubmit,
  onCancel,
  buttonLoading,
  inputLoading,
}) => {
  const snackbar = useSnackbar();
  const { databaseConnectorId } = useBackendConnectors();
  const { databaseMetrics = [], gettingDatabaseMetrics = false, magentoConnectorEnabled } =
    useConnectors();
  const [expandedMetricCollapseType, setExpandedMetricCollapseType] = useState<
    ConnectorType | null | MetricsByConnectorType
  >(null);
  const [tabValue, setTabValue] = useState(SettingTab.FORMATTING);
  const [openModal, setOpenModal] = useState(false);

  const {
    formState: { errors },
    reset,
    getValues,
    handleSubmit,
  } = useFormContext<CustomFormulaValues>();

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message = graphQLErrors.length ? graphQLErrors[0].message : errorText;

    snackbar(message);
  };

  const { data: facebookMetricsData, loading: gettingFacebookMetrics } =
    useQuery<Pick<Query, 'getFacebookMetrics'>, never>(GET_FACEBOOK_METRICS, {
      onError,
    });

  const { data: googleAdsMetricsData, loading: gettingGoogleAdsMetrics } =
    useQuery<Pick<Query, 'getGoogleAdsMetricsList'>, never>(
      GET_GOOGLE_ADS_METRICS,
      { onError },
    );

  const {
    data: googleAnalyticsMetricsData,
    loading: gettingGoogleAnalyticsMetrics,
  } = useQuery<Pick<Query, 'getGoogleAnalyticsMetrics'>, never>(
    GET_GOOGLE_ANALYTICS_METRICS,
    { onError },
  );

  const { data: shopifyMetricsData, loading: gettingShopifyMetrics } = useQuery<
    Pick<Query, 'getShopifyMetrics'>,
    never
  >(GET_SHOPIFY_METRICS, {
    onError,
    // Evita che la lista metriche resti "stuck" su cache quando aggiungiamo nuove metriche.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const { data: magentoMetricsData, loading: gettingMagentoMetrics } = useQuery<
    Pick<Query, 'getMagentoMetrics'>,
    never
  >(GET_MAGENTO_METRICS, {
    onError,
    skip: magentoConnectorEnabled !== true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const handleChangeTab = (_: React.SyntheticEvent, newValue: number) =>
    setTabValue(newValue);

  const toggleMetricCollapse = (
    type: ConnectorType | MetricsByConnectorType,
  ) => {
    setExpandedMetricCollapseType((prevType) =>
      prevType === type ? null : type,
    );
  };

  const checkKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const handleClickDeleteFiltersButton = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const clearAllFilters = () => {
    const form = getValues();
    const updatedFormulaItems = getValues('formulaItems').map((item) => {
      if (item.filters) {
        item.filters = [];
      }

      return item;
    });

    const formWithResettedFilters: CustomFormulaValues = {
      name: form.name,
      format: form.format,
      formulaItems: updatedFormulaItems,
    };

    reset(formWithResettedFilters);

    handleCloseModal();
  };

  let currentMetrics: Metric[] = [];

  switch (expandedMetricCollapseType) {
    case ConnectorType.FACEBOOK:
      currentMetrics = facebookMetricsData?.getFacebookMetrics.metrics || [];

      break;
    case ConnectorType.GOOGLE_ADS:
      currentMetrics =
        googleAdsMetricsData?.getGoogleAdsMetricsList.metrics || [];

      break;
    case ConnectorType.GOOGlE_ANALYTICS4:
      currentMetrics =
        googleAnalyticsMetricsData?.getGoogleAnalyticsMetrics.metrics || [];

      break;
    case ConnectorType.SHOPIFY:
      currentMetrics = shopifyMetricsData?.getShopifyMetrics.metrics || [];

      break;
    case ConnectorType.MAGENTO:
      currentMetrics = magentoMetricsData?.getMagentoMetrics.metrics || [];

      break;
    case ConnectorType.DATABASE:
      currentMetrics = databaseMetrics;

      break;
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)} onKeyDown={checkKeyDown}>
        <InputsWrapper>
          <Container>
            <TextInput
              label="Formula name"
              name="name"
              type="text"
              error={!!errors.name}
              helperText={errors.name?.message || ' '}
              loading={inputLoading}
            />
            <FormulaCalculator
              loading={inputLoading}
              options={currentMetrics}
            />
          </Container>
          <FormulaSettings>
            <Tabs
              value={tabValue}
              onChange={handleChangeTab}
              TabIndicatorProps={{ hidden: true }}
            >
              <Tab label="Formatting" />
              <Tab label="Filters" />
            </Tabs>
            {tabValue === SettingTab.FORMATTING && (
              <Formatting loading={!!inputLoading} />
            )}
            {tabValue === SettingTab.FILTERS && (
              <Filters
                onClickDelete={handleClickDeleteFiltersButton}
                loading={!!inputLoading}
              />
            )}
          </FormulaSettings>
          <Metrics
            facebookData={{
              metrics: facebookMetricsData?.getFacebookMetrics.metrics || [],
              loading: gettingFacebookMetrics,
            }}
            shopifyData={{
              metrics: shopifyMetricsData?.getShopifyMetrics.metrics || [],
              loading: gettingShopifyMetrics,
            }}
            magentoData={{
              metrics: magentoMetricsData?.getMagentoMetrics.metrics || [],
              loading: gettingMagentoMetrics,
            }}
            googleAdsData={{
              metrics:
                googleAdsMetricsData?.getGoogleAdsMetricsList.metrics.filter(
                  ({ category }) => category === 'metric',
                ) || [],
              loading: gettingGoogleAdsMetrics,
            }}
            googleAnalyticsData={{
              metrics:
                googleAnalyticsMetricsData?.getGoogleAnalyticsMetrics.metrics ||
                [],
              loading: gettingGoogleAnalyticsMetrics,
            }}
            databaseData={
              databaseConnectorId
                ? {
                    metrics: databaseMetrics,
                    loading: gettingDatabaseMetrics,
                  }
                : undefined
            }
            expandedCollapseType={expandedMetricCollapseType}
            onToggleCollapse={toggleMetricCollapse}
          />
        </InputsWrapper>
        <ButtonsWrapper>
          <Button type="button" fullWidth variant="outlined" onClick={onCancel}>
            cancel
          </Button>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            loading={buttonLoading}
          >
            Save
          </Button>
        </ButtonsWrapper>
      </Form>
      <DeleteModal
        open={openModal}
        title="Delete filters?"
        description="Are you sure you want to delete all yours filters?"
        onClose={handleCloseModal}
        onDelete={clearAllFilters}
      />
    </>
  );
};
